"use client";

/**
 * WardrobeGate — one app-wide "you need clothes in your wardrobe" prompt.
 *
 * Most Hoku features (daily drip, look lab, wardrobe shopping, …) only work
 * once the user has clothes saved. Instead of each feature dead-ending on an
 * error, any feature/hook calls `requireWardrobe()` to pop a small modal that
 * explains it and routes straight into the Add-to-Wardrobe flow (which leads
 * with the free "Pick from Catalog" option).
 *
 * Two entry points:
 *   - requireWardrobe(opts?) — show the explain-prompt, then the add flow.
 *   - openAddWardrobe(onAdded?) — skip the prompt, go straight to the add flow
 *     (use when the caller already shows its own "you need clothes" message).
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Shirt } from "lucide-react";
import dynamic from "next/dynamic";
import { useUser } from "@/shared/contexts/UserContext";
import { WARDROBE_MUTATED_EVENT } from "@/features/wardrobe/hooks/useWardrobe";

/**
 * The add-clothes flow drags in cropperjs + the camera UI + the catalog picker —
 * ~97KB of JS that shipped on EVERY route because this provider sits in the root
 * layout. Worse, the modals inside it mount their data hooks unconditionally, so a
 * page like /user/shop was firing wardrobe/catalog/basics (481KB!) and
 * billing/upload-quota on load, for a modal nobody had opened.
 *
 * Loaded on demand instead. See the mount gate below — dynamic() alone isn't
 * enough, because rendering the component is what triggers the import.
 */
const AddDressEntry = dynamic(
  () => import("@/features/wardrobe/modals/AddDressEntry").then((m) => m.AddDressEntry),
  { ssr: false },
);

interface GateOptions {
  /** Override the prompt title. */
  title?: string;
  /** Override the prompt body — say what the feature needs. */
  message?: string;
  /** Fires after the user successfully adds item(s). */
  onAdded?: (count?: number) => void;
}

interface WardrobeGateValue {
  /** Pop the explain-prompt, then route into the add flow. */
  requireWardrobe: (opts?: GateOptions) => void;
  /** Skip the prompt — open the add flow directly. */
  openAddWardrobe: (onAdded?: (count?: number) => void) => void;
}

const WardrobeGateContext = createContext<WardrobeGateValue | null>(null);

export function useWardrobeGate(): WardrobeGateValue {
  const ctx = useContext(WardrobeGateContext);
  if (!ctx) {
    throw new Error("useWardrobeGate must be used within a WardrobeGateProvider");
  }
  return ctx;
}

export function WardrobeGateProvider({ children }: { children: ReactNode }) {
  const { userId } = useUser();
  const [promptOpen, setPromptOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [opts, setOpts] = useState<GateOptions>({});
  const onAddedRef = useRef<GateOptions["onAdded"]>(undefined);

  // Mount the add-flow the first time it's opened, then LEAVE it mounted — which is
  // exactly how it behaved before. Unmounting on close would be tempting but would
  // kill an in-flight upload's state (AddDressModal tracks isUploading/isBusy), so
  // we only skip the mount for users who never open it at all. That's the whole win:
  // no cropper JS, no catalog-basics fetch, until the user actually taps Add.
  const [addEverOpened, setAddEverOpened] = useState(false);

  const requireWardrobe = useCallback((o: GateOptions = {}) => {
    setOpts(o);
    onAddedRef.current = o.onAdded;
    setPromptOpen(true);
  }, []);

  const openAddWardrobe = useCallback((onAdded?: (count?: number) => void) => {
    onAddedRef.current = onAdded;
    setPromptOpen(false);
    setAddEverOpened(true);
    setAddOpen(true);
  }, []);

  const startAdding = useCallback(() => {
    setPromptOpen(false);
    setAddEverOpened(true);
    setAddOpen(true);
  }, []);

  const handleSuccess = useCallback((count?: number) => {
    setAddOpen(false);
    // Let any mounted wardrobe consumer (dashboard, feature pages) refetch.
    try {
      window.dispatchEvent(new Event(WARDROBE_MUTATED_EVENT));
    } catch {
      /* SSR / no window — ignore */
    }
    onAddedRef.current?.(count);
    onAddedRef.current = undefined;
  }, []);

  return (
    <WardrobeGateContext.Provider value={{ requireWardrobe, openAddWardrobe }}>
      {children}

      <Dialog open={promptOpen} onOpenChange={(o) => { if (!o) setPromptOpen(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-xl border-2 border-[#4c4c4c] bg-[#acd285] shadow-[2px_2px_0_#4c4c4c]">
              <Shirt className="h-6 w-6 text-[#4c4c4c]" />
            </div>
            <DialogTitle className="font-winky text-xl text-[#4c4c4c]">
              {opts.title || "Add a few clothes you actually wear"}
            </DialogTitle>
            <DialogDescription className="font-winky text-sm text-[#6d6d6d]">
              {opts.message ||
                "Hoku will start learning your style memory. The more Hoku remembers, the better it protects you from wrong purchases."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2 pt-2">
            <button
              type="button"
              onClick={startAdding}
              className="flex items-center justify-center rounded-2xl border-2 border-[#4c4c4c] bg-[#acd285] p-3 font-winky text-base font-bold text-[#4c4c4c] shadow-[3px_3px_0_#4c4c4c] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
            >
              Add clothes
            </button>
            <button
              type="button"
              onClick={() => setPromptOpen(false)}
              className="font-winky text-sm text-[#6d6d6d] underline-offset-2 hover:underline"
            >
              Maybe later
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {addEverOpened ? (
        <AddDressEntry
          isOpen={addOpen}
          onClose={() => setAddOpen(false)}
          wardrobeId={userId || undefined}
          onSuccess={handleSuccess}
        />
      ) : null}
    </WardrobeGateContext.Provider>
  );
}
